/**
 * IIR evaluation API client (analyst 1–5 star reviews).
 * Auth headers follow collectionTaskApi (Bearer token).
 */
const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';

function authHeaders() {
    const token = localStorage.getItem('access_token');
    return token
        ? {'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'}
        : {'Content-Type': 'application/json'};
}

async function apiFetch(path, opts = {}) {
    const resp = await fetch(`${BASE}${path}`, {headers: authHeaders(), ...opts});
    if (resp.status === 404) return null;
    if (!resp.ok) {
        let detail = `${opts.method || 'GET'} ${path} → ${resp.status}`;
        try {
            const body = await resp.json();
            if (body?.error) detail = body.error;
            else if (body?.message) detail = body.message;
        } catch {
            /* ignore */
        }
        throw new Error(detail);
    }
    if (resp.status === 204) return null;
    return resp.json();
}

/**
 * @param {string} reportId
 * @returns {Promise<{
 *   summary: {averageScore: number|null, count: number},
 *   evaluations: Array<{id?: string|number, reviewerUserId?: string, reviewerDisplayName?: string, score: number, comment: string, createdAt?: string, updatedAt?: string}>,
 *   mine: {score: number, comment: string}|null,
 *   canEvaluate: boolean,
 * }|null>}
 */
export async function getIirEvaluations(reportId) {
    if (!reportId) return null;
    return apiFetch(`/api/collection/iirs/${encodeURIComponent(reportId)}/evaluations`);
}

/**
 * Upsert the current user's evaluation for an IIR.
 * @param {string} reportId
 * @param {{score: number, comment: string}} payload
 */
export async function upsertMyIirEvaluation(reportId, {score, comment} = {}) {
    if (!reportId) throw new Error('reportId is required');
    return apiFetch(`/api/collection/iirs/${encodeURIComponent(reportId)}/evaluations/mine`, {
        method: 'PUT',
        body: JSON.stringify({score, comment: comment ?? ''}),
    });
}

/**
 * Delete the current user's evaluation for an IIR.
 * @param {string} reportId
 */
export async function deleteMyIirEvaluation(reportId) {
    if (!reportId) throw new Error('reportId is required');
    return apiFetch(`/api/collection/iirs/${encodeURIComponent(reportId)}/evaluations/mine`, {
        method: 'DELETE',
    });
}
